import {
  ApiSubscriptionStatus,
  ApiSubscriptionInvoice,
  ApiPlan,
  SubscriptionInvoice,
  SubscriptionPlanMatrix,
  ApiSubscriptionPeriod,
  SubscriptionPeriod,
  SubscriptionPaymentStatus
} from 'Types'

import { default as locale } from 'date-fns__legacy__/locale/pt'
import localeBrazil from 'date-fns/locale/pt-BR'
import addMonths from 'date-fns__legacy__/add_months'
import format from 'date-fns__legacy__/format'
import formatDistanceStrict from 'date-fns/formatDistanceStrict'
import getDate from 'date-fns__legacy__/get_date'
import orderBy from 'lodash/orderBy'
import parse from 'date-fns__legacy__/parse'
import setDate from 'date-fns__legacy__/set_date'
import startOfToday from 'date-fns__legacy__/start_of_today'
import appPlanList, { AppPlan } from 'Services/appPlanList'
import creditCardDetails from 'Services/creditCardDetails'
import invoiceStatus from 'Enums/invoiceStatus'
import paymentTypes from 'Enums/paymentTypes'
import periodDates from 'Enums/periodDates'
import planBenefitTypes from 'Enums/planBenefitTypes'
import planContainerTypes from 'Enums/planContainerTypes'
import subscriptionPaymentStatus from 'Enums/subscriptionPaymentStatus'
import subscriptionPeriods from 'Enums/subscriptionPeriods'

// #region Subscription Plans
const parsePrice = (priceFormat: string, container: number): string | number => {
  const priceRaw = priceFormat.replace(/[a-zA-Z]|[$]|\s/g, '')
  const priceNumber = parseFloat(priceRaw)

  if (container === planContainerTypes.demand) {
    return 'Gratuito'
  } else if (container === planContainerTypes.diamond) {
    return 'Sob consulta'
  } else {
    return priceNumber
  }
}

const parsePriceNew = (priceFormat: string) => {
  const priceRaw = priceFormat.replace(/[a-zA-Z]|[$]|\s/g, '')
  const priceNumber = priceRaw.split(',')

  return priceRaw
}

export const parsePlan = (apiPlan: ApiPlan): AppPlan => {
  const appPlan = appPlanList.find(appPlan => apiPlan.container === appPlan.container)
  const planPrice = parsePrice(apiPlan.amount, appPlan.container)

  return Object.create(appPlan)
    .withID(apiPlan.id)
    .withPrice(planPrice)
}

export const parsePlanNew = (currentPlan: ApiPlan): AppPlan => {
  const appPlan = appPlanList.find(appPlan => currentPlan.container === appPlan.container)
  const planPrice = parsePriceNew(currentPlan.estimatedAmountPerMonth)
  const planPriceTotal = parsePriceNew(currentPlan.estimatedAmount)
  const montlyPrice = parsePriceNew(currentPlan.amountByPeriods[0].estimatedAmount)
  
  return Object.create(appPlan)
    .withID(currentPlan.id)
    .withPrice(planPrice)
    .withMonthlyPrice(montlyPrice)
    .withPriceTotal(planPriceTotal);
}

export const parsePlanList = (apiPlanList: ApiPlan[]): AppPlan[] => {
  const activePlanList = apiPlanList.filter(apiPlan => apiPlan.status === 'Ativo')
  const orderedPlanList = orderBy(activePlanList, ['container'], ['desc'])

  return orderedPlanList.map(parsePlan)
}

export const parsePlanListNew = (apiPlanList: ApiPlan[]): AppPlan[] => {
  const activePlanList = apiPlanList.filter(apiPlan => apiPlan.status === 'Ativo')
  const orderedPlanList = orderBy(activePlanList, ['container'], ['desc'])

  return orderedPlanList.map((plan) => parsePlanNew(plan))
}

/** Return a row containing plan information, with the first row being the description */
const buildBenefitRow = (planList: AppPlan, benefitType: string): any[] => {
  const benefitList = Object.values(planList).map(item => item[benefitType])
  const benefitLabel = planBenefitTypes.getDescriptionByValue(benefitType)

  return [ benefitLabel, ...benefitList ]
}

export const parsePlanMatrix = (planList: AppPlan): SubscriptionPlanMatrix => ({
  identifier: [
    buildBenefitRow(planList, planBenefitTypes.id)
  ],

  name: [
    buildBenefitRow(planList, planBenefitTypes.name)
  ],

  price: [
    buildBenefitRow(planList, planBenefitTypes.price)
  ],

  benefit: [
    buildBenefitRow(planList, planBenefitTypes.priority),
    buildBenefitRow(planList, planBenefitTypes.discount),
    buildBenefitRow(planList, planBenefitTypes.bonus),
    buildBenefitRow(planList, planBenefitTypes.category),
    buildBenefitRow(planList, planBenefitTypes.city),
    buildBenefitRow(planList, planBenefitTypes.position),
    buildBenefitRow(planList, planBenefitTypes.support),
  ],
})

export const parsePeriod = (data: ApiSubscriptionPeriod): SubscriptionPeriod => {
  const parseSuffix = (name: string) => ({
    Mensal: 'mês',
    Trimestral: 'trimestre',
    Semestral: 'semestre',
    Anual: 'ano',
  }[name])

  return {
    id: data.id,
    name: data.period,
    suffix: parseSuffix(data.period) || null,
    discount: data.percentageDiscount,
  }
}
// #endregion

// #region Subscription Management
const getPaymentStatusType = (value: number): number => {
  let status = invoiceStatus.none

  const waitingStatus = [
    subscriptionPaymentStatus.authorized,
    subscriptionPaymentStatus.waitingPayment,
    subscriptionPaymentStatus.pendingRefund,
    subscriptionPaymentStatus.processing,
    subscriptionPaymentStatus.pendingReview,
    subscriptionPaymentStatus.open,
    subscriptionPaymentStatus.analyzing,
    subscriptionPaymentStatus.chargePending,
    subscriptionPaymentStatus.chargeProcessing,
  ]

  const successStatus = [
    subscriptionPaymentStatus.paid,
    subscriptionPaymentStatus.refunded,
    subscriptionPaymentStatus.chargePaid,
    subscriptionPaymentStatus.chargeRefunded,
  ]

  const errorStatus = [
    subscriptionPaymentStatus.none,
    subscriptionPaymentStatus.cancelled,
    subscriptionPaymentStatus.boletoExpired,
    subscriptionPaymentStatus.chargedback,
    subscriptionPaymentStatus.chargeChargeback,
  ]

  const refusedStatus = [
    subscriptionPaymentStatus.refused,
    subscriptionPaymentStatus.chargePaymentFailed,
  ]

  if (successStatus.includes(value)) {
    status = invoiceStatus.success
  }
  else if (waitingStatus.includes(value)) {
    status = invoiceStatus.waiting
  }
  else if (errorStatus.includes(value)) {
    status = invoiceStatus.error
  }
  else if (refusedStatus.includes(value)) {
    status = invoiceStatus.refused
  }

  return status
}

export const getPaymentStatus = (status: number): SubscriptionPaymentStatus => {
  return {
    id: status,
    description: subscriptionPaymentStatus.getDescriptionByValue(status),
    type: getPaymentStatusType(status)
  }
}

 export const paymentFormat = (value) => {

  const getStatusString = (status) => ({
    0: 'none',
    1: 'succes',
    2: 'warning',
    3: 'error',
    4: 'refused',
  }[status])

  const status = subscriptionPaymentStatus.toList().find(item => item.value === value)

  const description = status.description
  const type = getPaymentStatusType(status.value)

  return { description, type: getStatusString(type) }
}

const parseExpiration = (expirationDate) => {
  const expiration = parse(expirationDate)

  return format(expiration, 'DD [de] MMMM [de] YYYY', { locale })
}

/** Predict the next subscription renew based on the start of the subscription and its frequency */
const predictNextInvoice = (subscriptionDate: Date, renewPeriod: number): Date => {
  const subscriptionDateAsToday = setDate(startOfToday(), getDate(subscriptionDate))
  const predictedInvoiceDate = addMonths(subscriptionDateAsToday, renewPeriod)

  return predictedInvoiceDate
}

/** Returns a formated date of the next subscription renew date */
const parseNextInvoice = (subscriptionDate: Date, renewPeriod: number): string => {
  const predictedInvoiceDate = addMonths(subscriptionDate, renewPeriod)

  const nextInvoiceDate = predictedInvoiceDate < startOfToday()
    ? predictNextInvoice(subscriptionDate, renewPeriod)
    : predictedInvoiceDate

  return format(nextInvoiceDate, 'DD [de] MMMM [de] YYYY', { locale })
}

const parseCreditCard = (data: ApiSubscriptionStatus) => {
  if (data.paymentMethod !== paymentTypes.creditCard)
    return null

  const cardDetail = creditCardDetails(data.cardBrand)

  const digits = data.cardNumber ? data.cardNumber.substring(data.cardNumber.indexOf('-') + 1) : '';

  return {
    id: data.cardId || 0,
    digits: digits,
    brand: cardDetail.brand,
    icon: cardDetail.icon,
  }
}

const parsePaymentMethod = (data: ApiSubscriptionStatus) => ({
  id: data.paymentMethod,
  name: data.paymentMethodFormat,
})

export const parsePlanStatus = (data: ApiSubscriptionStatus) => {
  const dateCreated = parse(data.dateCreated)
  const periodName = subscriptionPeriods.getKeyByValue(data.period)
  const periodInMonths = periodDates[periodName] || periodDates.monthly

  return {
    planName: data.name,
    container: data.container,
    amount: data.amountFormat,
    period: periodDates.getDescription(periodName),
    nextInvoice: parseNextInvoice(dateCreated, periodInMonths),
    paymentMethod: parsePaymentMethod(data),
    creditCard: parseCreditCard(data),
    billing: !data.notGenerateBilling,
  }
}

export const parseInvoice = (data: ApiSubscriptionInvoice): SubscriptionInvoice => {
  return {
    id: data.billId,
    barcode: data.boletoBarcode,
    barcodeUrl: data.boletoUrl,
    pixCopyPasteCode: data.pixCopyPasteCode,
    pixQrCode: data.pixQrCode,
    expirationDateFormat: data.expirationDateFormat,
    plan: data.plan,
    expiration: parseExpiration(data.expirationDate),
    amount: data.amountFormat,
    status: getPaymentStatus(data.status),
    nfeLinkPdf: data.nfeLinkPdf,
  }
}

export const updatePeriodList = (period, plan) => {
    const selectedAmount = plan.amountByPeriods.find(amount => amount.period === period);
    return {
      ...plan,
      ...selectedAmount

    };
}


export const updatePeriodListArray = (period, plans) => {
  return plans.map(plan => {
    const selectedAmount = plan.amountByPeriods.find(amount => amount.period === period);
    return {
      ...plan,
      ...selectedAmount

    };
  }).filter(plan => plan.amountByPeriods.length > 0);
}


// #endregion

export default {
  parsePlanList,
  parsePlanMatrix,
  parsePlanStatus,
  getPaymentStatus,
  paymentFormat,
  updatePeriodList,
  updatePeriodListArray
}